import type { ReactNode } from 'react';
import type { Broker } from '@/mocks/brokers';
import { brokerRecommendation, brokers } from '@/mocks/brokers';
import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';
import BrokerButtons from '@/pages/forex-trading/components/BrokerButtons';

const factTitles = brokerRecommendation.detailFactTitles;

function FactBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-heading text-[13px] font-semibold text-foreground-950">{title}</h4>
        <span className="rounded-full bg-background-200/80 px-2 py-0.5 text-[10px] font-medium text-foreground-600">
          {brokerRecommendation.pendingLabel}
        </span>
      </div>
      <div className="mt-2 text-[13px] leading-relaxed text-foreground-700">{children}</div>
    </div>
  );
}

function BrokerDetailPanel({ broker }: { broker: Broker }) {
  return (
    <article
      id={broker.anchor}
      className="scroll-mt-24 rounded-xl border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <img
            src={broker.logo}
            alt={`${broker.name} logo`}
            title={broker.name}
            className="h-12 w-12 shrink-0 rounded-lg border border-background-200 bg-background-100 object-contain"
          />
          <div>
            <h3 className="font-heading text-xl font-semibold leading-tight text-foreground-950">{broker.name}</h3>
            <p className="mt-0.5 text-xs text-foreground-500">{broker.tagline}</p>
          </div>
        </div>
        <a
          href="#brokers"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <i className="ri-arrow-up-line text-sm" />
          กลับไปตารางเปรียบเทียบ
        </a>
      </div>

      <div className="mt-5 border-t border-background-200 pt-5">
        <h4 className="font-heading text-[13px] font-semibold uppercase tracking-[0.1em] text-foreground-500">
          ภาพรวมแบรนด์
        </h4>
        <div className="mt-2.5 flex flex-col gap-2.5">
          {broker.detail.brand.map((paragraph) => (
            <p key={paragraph.slice(0, 24)} className="text-sm leading-relaxed text-foreground-700">
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-heading text-[13px] font-semibold text-foreground-950">
              {broker.detail.regulator.label}
            </h4>
            <span className="rounded-full bg-background-200/80 px-2 py-0.5 text-[10px] font-medium text-foreground-600">
              {brokerRecommendation.pendingLabel}
            </span>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-foreground-700">{broker.detail.regulator.value}</p>
          <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {broker.detail.entities.map((entity) => (
              <li key={entity} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
                <i className="ri-checkbox-blank-circle-line mt-0.5 text-[10px] text-primary-600" />
                {entity}
              </li>
            ))}
          </ul>
        </div>

        <FactBlock title={factTitles.accounts}>{broker.detail.accounts}</FactBlock>
        <FactBlock title={factTitles.platforms}>{broker.detail.platforms}</FactBlock>
        <FactBlock title={factTitles.cost}>{broker.detail.cost}</FactBlock>
        <FactBlock title={factTitles.funding}>{broker.detail.funding}</FactBlock>
      </div>

      <div className="mt-3 rounded-lg border border-accent-200 border-l-2 border-l-accent-500 bg-accent-50/70 p-4">
        <h4 className="flex items-center gap-2 font-heading text-[13px] font-semibold text-foreground-950">
          <i className="ri-alert-line text-sm text-accent-700" />
          เงื่อนไขที่ควรระวัง
        </h4>
        <ul className="mt-2 flex flex-col gap-1.5">
          {broker.detail.cautions.map((caution) => (
            <li key={caution} className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground-700">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent-600" />
              {caution}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 flex flex-col gap-4 border-t border-background-200 pt-5 md:flex-row md:items-center md:justify-between">
        <div className="w-full md:max-w-md">
          <BrokerButtons broker={broker} variant="detail" />
        </div>
        <div className="text-left md:text-right">
          <p className="flex items-center gap-1.5 text-[11px] font-medium text-foreground-500 md:justify-end">
            <i className="ri-link text-sm" />
            เว็บไซต์ทางการและลิงก์เปิดบัญชี: รอยืนยัน
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-foreground-400">
            จะแสดงเฉพาะลิงก์ที่ตรวจสอบกับเว็บไซต์ของโบรกเกอร์แล้วเท่านั้น
          </p>
        </div>
      </div>
    </article>
  );
}

export default function BrokerDetails() {
  return (
    <section className="bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="รายละเอียด"
          title={brokerRecommendation.detailTitle}
          subtitle={brokerRecommendation.detailSubtitle}
        />

        <div className="mt-8 flex flex-col gap-5">
          {brokers.map((broker, index) => (
            <ScrollReveal key={broker.id} delay={index * 60}>
              <BrokerDetailPanel broker={broker} />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}